import React, { Children, useCallback, useEffect, useState } from 'react';
import { Stack, Grid, Typography, Box, Skeleton } from '@mui/material';
import CustomKFSearchBar from 'modules/hiring/components/customkfSearchBar';
import CandidatePipeLineTable from 'modules/hiring/components/candidatePipeLineTable';
import {
  IRequisition,
  RequisitionStage,
} from 'modules/hiring/models/myOpenRequisition.model';
import { useTranslation } from 'react-i18next';

import IStoreState from 'store/IStoreState';
import { connect } from 'react-redux';
import {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
} from 'modules/hiring/stateManagement/thunks';
import { TSelectedOpenRequisitionState } from 'modules/hiring/stateManagement/state';
import Tabs from 'shared/components/Tabs';
import {
  HiringActivity,
  TCandidatePipelineResponse,
  TPipelineCountItem,
} from 'modules/hiring/models/candidatePipeline.model';
import {
  customEventSubscribe,
  customEventUnsubscribe,
} from 'modules/hiring/utilities';
import {
  IReqCandidatesPayload,
  IReqCandidatesResponse,
} from 'modules/hiring/models/IRequisitionCandidates.interface';
import {
  axiosCandidatesPipeline,
  axiosRequisitionCandidates,
} from 'modules/hiring/stateManagement/api';
import { Canceler } from 'axios';
import Interviewers from 'modules/hiring/components/interviewersPanel/interviewersPanel';
import HiringBreadCrumb from 'modules/hiring/components/hiringBreadCrumb';
import HiringHeader from 'modules/hiring/components/hiringHeader';
import { ReactComponent as MapPin } from 'assets/map-pin.svg';
import CandidateStageTile from 'modules/hiring/components/CandidateStageTile';
import DropdownWithSearch from 'shared/components/dropdownWithSearch';

type TCandidatePipelinePageStateProps = ReturnType<typeof mapStateToProps>;
type TCandidatePipelinePageDispatchProps = {
  getMyOpenRolesData: () => void;
  setSelectedOpenRequisition: (
    requisition: TSelectedOpenRequisitionState
  ) => void;
};

interface ICandidatePipelinePageProps
  extends TCandidatePipelinePageStateProps,
    TCandidatePipelinePageDispatchProps {}

const CandidatePipelinePage: React.FC<ICandidatePipelinePageProps> = ({
  getMyOpenRolesData: getMyOpenRoles,
  myOpenRoles,
  currentRequisition,
  setSelectedOpenRequisition: setCurrentRequisition,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [pageRecordsCount, setPageRecordsCount] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [isCandidateListLoading, setIsCandidateListLoading] = useState(false);
  const [isCandidatesPipelineLoading, setIsCandidatesPipelineLoading] =
    useState(false);
  const [candidatesListData, setCandidatesListData] =
    useState<IReqCandidatesResponse>();
  const [candidatesPipelineData, setCandidatesPipelineData] =
    useState<TCandidatePipelineResponse>();
  let candidateListcanceler: Canceler | undefined = undefined;
  let candidatePipelinecanceler: Canceler | undefined = undefined;
  const apiPayload: IReqCandidatesPayload = {
    searchKeywords: '',
    pageRecordsCount: 1000, // TBD
    pageIndex: 1,
    applicationStatus: '',
    isAscending: true,
    isHiringManager: true,
    jobTitle: [],
    location: [],
    sortName: '',
    stages: [],
  };
  const { t: translate } = useTranslation();
  const { data: yourOpenRolesData } = myOpenRoles;
  const handleTypingStopped = (str: string) => {
    setSearchTerm(str);
    setPageIndex(0);
  };
  const getFilteredActivityDropdownList = (data: TPipelineCountItem[]) => {
    const activityFilter = [
      HiringActivity.Added,
      HiringActivity.InvitedToApply,
      HiringActivity.Applied,
      HiringActivity.Sourced,
      HiringActivity.Screen,
    ];
    return data?.filter(
      (item) => !activityFilter?.includes(item.activityName as HiringActivity)
    );
  };

  const reqSummaryWidth = currentRequisition ? 400 : 0;
  const [currentTab, setCurrentTab] = useState('0');

  const allTab = {
    i18nKey: 'All',
    count: getFilteredActivityDropdownList(
      candidatesPipelineData?.pipelineCountData ?? []
    ).reduce((total, item) => total + item.count, 0),
  };

  const [tabs, setTabs] = useState<
    {
      i18nKey: string;
      count: number;
    }[]
  >([allTab]);

  const updatePayload = useCallback(
    (payload: IReqCandidatesPayload) => {
      /* istanbul ignore else */
      let activityName: string = tabs[parseInt(currentTab)].i18nKey;
      if (activityName && activityName !== 'All') {
        payload.stages.push(activityName);
      }
      return payload;
    },
    [currentTab, tabs]
  );

  const getPayloadWithSearch = useCallback(() => {
    return {
      ...updatePayload(apiPayload),
      pageIndex: pageIndex + 1,
      pageRecordsCount,
      searchKeywords: searchTerm,
    };
  }, [
    searchTerm,
    updatePayload,
    pageIndex,
    pageRecordsCount,
    currentRequisition,
  ]);

  const getCandidateList = useCallback(async () => {
    setIsCandidateListLoading(true);
    const payload = getPayloadWithSearch();
    let response = await axiosRequisitionCandidates(
      currentRequisition?.requisitionID,
      payload,
      (cancel: Canceler) => (candidateListcanceler = cancel)
    );
    response = {
      ...response,
      candidateList: [...(response?.candidateList || [])]?.sort(
        (a: any, b: any) => a.name.localeCompare(b.name)
      ),
    };
    setIsCandidateListLoading(false);
    setCandidatesListData(() => response);
  }, [getPayloadWithSearch]);

  const getPipelineCandidatesList = useCallback(async () => {
    setCurrentTab('0');
    setIsCandidatesPipelineLoading(true);
    const payload = updatePayload(apiPayload);
    const responsePipeline = await axiosCandidatesPipeline(
      currentRequisition?.requisitionID,
      payload,
      (cancel: Canceler) => (candidatePipelinecanceler = cancel)
    );
    setIsCandidatesPipelineLoading(false);
    setCandidatesPipelineData(() => responsePipeline);
  }, [currentRequisition]);

  useEffect(() => {
    getCandidateList();
    return () => {
      candidateListcanceler?.();
    };
  }, [getCandidateList, candidateListcanceler]);

  useEffect(() => {
    getPipelineCandidatesList();
    return () => {
      candidatePipelinecanceler?.();
    };
  }, [candidatePipelinecanceler, getPipelineCandidatesList]);

  const makeDecisionSuccess = useCallback(() => {
    getCandidateList();
    getPipelineCandidatesList();
  }, [getCandidateList, getPipelineCandidatesList]);

  useEffect(() => {
    /* istanbul ignore else */
    getMyOpenRoles();
    customEventSubscribe('makeDecision', makeDecisionSuccess);
    return () => {
      customEventUnsubscribe('makeDecision', makeDecisionSuccess);
    };
  }, [makeDecisionSuccess]);

  const breadCrumbItems = [{ title: translate('hiring'), path: '../overview' }];

  const onDropdownApplyClick = (requisitionID: IRequisition | null) => {
    setCurrentRequisition(requisitionID);
  };
  useEffect(() => {
    if (candidatesPipelineData) {
      const filteredData = getFilteredActivityDropdownList(
        candidatesPipelineData.pipelineCountData
      );

      const newTabs = [
        allTab,
        ...filteredData.map((item) => ({
          i18nKey: item.activityName || '',
          count: item.count || 0,
        })),
      ];

      setTabs(newTabs);
    }
  }, [candidatesPipelineData]);

  const onStepTabChange = (_: any, newValue: string) => {
    setPageIndex(0);
    setCurrentTab(newValue);
  };

  const location = [
    currentRequisition?.city,
    currentRequisition?.state,
    currentRequisition?.country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Grid container>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <HiringBreadCrumb
          id='CandidatePipelineBreadCrumb'
          breadCrumbItems={breadCrumbItems}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ mb: ({ typography }) => typography.pxToRem(20) }}
      >
        {yourOpenRolesData && (
          <Grid sx={{ mb: ({ typography }) => typography.pxToRem(8) }}>
            <DropdownWithSearch
              id='dropdown-with-search'
              data-testid='dropdown-with-search'
              options={yourOpenRolesData?.requisitionList?.filter(
                (req: IRequisition) =>
                  req.currentStage === RequisitionStage.Hiring
              )}
              defaultSelected={currentRequisition}
              handleSelection={onDropdownApplyClick}
            />
          </Grid>
        )}
        <HiringHeader
          id='CandidatePipelineHeader'
          label={
            currentRequisition
              ? `${currentRequisition?.jobTitle} #${currentRequisition?.requisitionID}`
              : translate('allCandidates')
          }
        />
        {currentRequisition && location && (
          <Box
            sx={{
              display: 'flex',
              mt: '0.5rem',
              mb: 0,
              alignItems: 'center',
            }}
          >
            <MapPin id={`locationPin`} data-testid={`locationPin`} />
            <Typography
              className='kfic2-bodyText'
              id='location'
              data-testid='location'
              sx={{ ml: '0.5rem', '&:focus': { width: 'fit-content' } }}
            >
              {location}
            </Typography>
          </Box>
        )}
      </Grid>
      <Stack
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: { xs: 'column', md: 'row' },
          mb: '1.5rem',
        }}
      >
        <Stack
          sx={{
            pr: currentRequisition ? { xs: 0, md: '1.5rem' } : 0,
            width: '100%',
            maxWidth: { xs: '100%', md: `calc(100% - ${reqSummaryWidth}px)` },
            overflowX: { xs: 'auto', md: 'visible' },
            whiteSpace: 'nowrap',
          }}
        >
          <CandidateStageTile
            stages={candidatesPipelineData?.pipelineCountData ?? []}
            loader={isCandidatesPipelineLoading}
          />
        </Stack>
        {currentRequisition && (
          <Stack
            id='candidate-pipeline-summary-interview'
            data-testid='candidate-pipeline-summary-interview'
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: `${reqSummaryWidth}px` },
              mt: { xs: '1rem', md: 0 },
            }}
          >
            <Interviewers
              id='interviewersWidget'
              requisitionId={currentRequisition?.requisitionID}
              jobTitle={candidatesListData?.jobTitle || ''}
              hiringManagerEmail={currentRequisition?.hiringManagerName || ''}
              currentStep={currentRequisition?.currentStep}
              sx={{
                position: 'relative',
                minHeight: '13.875rem',
                maxHeight: '13.875rem',
              }}
            />
          </Stack>
        )}
      </Stack>
      <Grid item xs={12}>
        <Stack
          id='candidate-pipeline-page-container'
          data-testid='candidate-pipeline-page-container'
          gap='1.25rem'
        >
          <Grid
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              width: '100%',
            }}
          >
            <Grid item>
              {!isCandidatesPipelineLoading ? (
                <Tabs
                  gridSx={{ mt: 0 }}
                  currentTab={currentTab}
                  setCurrentTab={onStepTabChange}
                  tabs={tabs}
                  tabPanelWidth={{ width: 'auto' }}
                  sectionName='candidate-table-steps-section'
                  tabListItemSx={{
                    opacity: '1',
                    textTransform: 'initial',
                    '& .MuiTypography-root': {
                      fontFamily: 'Lato',
                      fontSize: '1.125rem',
                      padding: '8px 0',
                    },
                  }}
                  disableTabs={true}
                  tabIndicatorStyleProps={{
                    height: '0.1875rem',
                    borderRadius: '6.25rem',
                  }}
                />
              ) : (
                <Grid sx={{ display: 'flex', flexDirection: 'row' }}>
                  {Array.from({ length: 4 }).map((_, index) => (
                    <Skeleton
                      key={`loader ${index}`}
                      variant='rectangular'
                      animation='wave'
                      sx={{
                        height: '2rem',
                        width: '7rem',
                        ml: '0.5rem',
                        bgcolor: (theme) => theme.palette.neutral?.main,
                      }}
                    />
                  ))}
                </Grid>
              )}
            </Grid>

            <Grid
              item
              sx={{
                width: { xs: '100%', md: '26.25rem' },
                mt: { xs: '1rem', md: 0 },
              }}
            >
              <CustomKFSearchBar
                placeholder={translate('searchByCandidateNameOrEmail')}
                onTypingStopped={handleTypingStopped}
                inputProps={{ 'data-testid': 'candidates-table-searchBar' }}
              />
            </Grid>
          </Grid>

          <Box sx={{ overflowX: 'auto' }}>
            <CandidatePipeLineTable
              requisitionCandidates={candidatesListData?.candidateList}
              page={pageIndex}
              onPageChange={setPageIndex}
              pageSize={pageRecordsCount}
              onPageSizeChange={setPageRecordsCount}
              rowCount={candidatesListData?.totalCount ?? 0}
              disableVirtualization
              showLoader={isCandidateListLoading}
            />
          </Box>
        </Stack>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = (state: IStoreState) => ({
  myOpenRoles: state.myOpenRoles,
  currentRequisition: state.selectedOpenRequisition,
});

const mapDispatchToProps = {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CandidatePipelinePage);
